import BookingForm from "@/components/BookingForm";
import SharedNavbar from "@/components/SharedNavbar";
import { useState } from "react";
import { Wrench, Clock, CheckCircle2, ArrowRight, Phone, Star, QrCode } from "lucide-react";

const QR_URL =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-23/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/qr_montage_v2_9c17edbeee4f4905ad6b9db9f5e4c739.png";

/* @section: montage-logo */
const LOGO_URL =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-23/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/ds_logo_transparent_1343324419e041449c280c0bfa45d1f3.png";

/* @section: montage-packages */
const packages = [
  {
    id: "1h",
    duration: "1 Stunde",
    price: 199,
    hours: 1,
    desc: "Ideal für kleine Montagearbeiten: einzelne Regale, Schränke, Beistelltische oder kleinere Demontagen.",
    popular: false,
  },
  {
    id: "2h",
    duration: "2 Stunden",
    price: 349,
    hours: 2,
    desc: "Perfekt für mittlere Möbel: Kleiderschränke, Bücherregale, Esstische mit Stühlen.",
    popular: true,
  },
  {
    id: "halbtag",
    duration: "Halber Tag",
    subtext: "max. 4 Stunden",
    price: 399,
    hours: 4,
    desc: "Für grössere Montagen: Schlafzimmer-Sets, kombinierte Wohnzimmermöbel oder mehrere Einzelteile.",
    popular: false,
  },
  {
    id: "ganztag",
    duration: "Ganzer Tag",
    subtext: "8 Stunden",
    price: 699,
    hours: 8,
    desc: "Komplette Einrichtungen, Umzüge mit Montage oder grosse Möbelmengen — wir kümmern uns um alles.",
    popular: false,
  },
];

export default function Montage() {
  const [selected, setSelected] = useState("2h");
  const [submitted, setSubmitted] = useState(false);

  const pkg = packages.find((p) => p.id === selected)!;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = fd.get("name");
    const phone = fd.get("phone");
    const email = fd.get("email");
    const address = fd.get("address");
    const date = fd.get("date");
    const notes = fd.get("notes");
    const subject = encodeURIComponent(
      `Montage-Anfrage: ${pkg.duration} (CHF ${pkg.price}) — ${name}`
    );
    const body = encodeURIComponent(
      `Montage-Anfrage\n` +
        `================\n` +
        `Name: ${name}\n` +
        `Telefon: ${phone}\n` +
        `E-Mail: ${email}\n` +
        `Adresse: ${address}\n` +
        `Gewünschtes Paket: ${pkg.duration} — CHF ${pkg.price}\n` +
        `Wunschdatum: ${date}\n` +
        `Bemerkungen: ${notes}\n\n` +
        `Gesendet via DS Logistik Montage-Service`
    );
    // Save to localStorage for admin panel
    const reqs = JSON.parse(
      typeof window !== "undefined"
        ? localStorage.getItem("dslogistik_requests") || "[]"
        : "[]"
    );
    reqs.push({
      id: Date.now(),
      date: new Date().toISOString(),
      name,
      phone,
      email,
      services: [`Montage: ${pkg.duration} CHF ${pkg.price}`],
      message: `Adresse: ${address} | Datum: ${date} | ${notes}`,
      status: "Neu",
    });
    if (typeof window !== "undefined") {
      localStorage.setItem("dslogistik_requests", JSON.stringify(reqs));
    }
    window.location.href = `mailto:andre.stalder@ds-logistik.ch?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <SharedNavbar />

      {/* @section: montage-hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 py-16 text-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white/80">
              <Wrench className="h-3.5 w-3.5 text-red-400" />
              Professionelle Möbelmontage
            </div>
            <h1 className="text-4xl font-bold leading-tight sm:text-5xl">
              Möbel zuhause montieren lassen
            </h1>
            <p className="mt-4 text-lg text-white/75">
              Haben Sie Möbel, die Sie nicht selbst aufbauen können oder
              möchten? Unsere professionellen Monteure kommen zu Ihnen nach
              Hause — pünktlich, sauber und mit Erfahrung.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <button
                onClick={() => { const el = document.getElementById("termin-anfragen"); if(el){ el.scrollIntoView({behavior:"smooth",block:"start"}); setTimeout(()=>{ const f = document.getElementById("booking-name"); if(f) f.focus(); }, 600); }}}
                className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-6 py-3 text-sm font-bold shadow-lg hover:-translate-y-px transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/></svg>
                Jetzt anfragen
              </button>
              {[
                "Erfahrene Fachkräfte",
                "Sauber & pünktlich",
                "Alle Möbelmarken",
                "Fixpreise — keine Überraschungen",
              ].map((f) => (
                <div key={f} className="flex items-center gap-1.5 text-white/80">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* @section: montage-pricing */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">

          {/* Header — links + rechts wie Screenshot */}
          <div className="grid gap-10 lg:grid-cols-2 lg:items-start mb-14">
            {/* Links: Info */}
            <div className="rounded-3xl bg-primary/5 border border-primary/20 p-8">
              <div className="space-y-7">
                {[
                  { icon: "🔨", title: "Einsatz von Schreinern", text: "Professionelle Fachkräfte für hochwertige Montage- und Demontagearbeiten direkt beim Kunden vor Ort." },
                  { icon: "🛋️", title: "Vielseitiger Service", text: "Ideal für anspruchsvolle Möbelmontagen, Spezialanpassungen oder den fachgerechten Rückbau bestehender Einrichtung." },
                  { icon: "✅", title: "Qualitätsgarantie", text: "Fachmännische Ausführung reduziert Reklamationen und sichert die Langlebigkeit der Möbelstücke." },
                ].map(({ icon, title, text }) => (
                  <div key={title} className="flex gap-4 items-start">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xl">
                      {icon}
                    </div>
                    <div>
                      <p className="font-bold text-foreground">{title}</p>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Rechts: 2×2 Preiskarten */}
            <div>
              <h3 className="text-xl font-bold text-foreground mb-1">Flexible Preismodelle</h3>
              <div className="h-0.5 w-full bg-primary mb-6" />
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "EINSTIEG",             price: "199", unit: "CHF", sub: "1. Stunde pro Schreiner" },
                  { label: "FOLGESTUNDE",           price: "349", unit: "CHF", sub: "Ab der 2. Stunde" },
                  { label: "HALBTAGES-PAUSCHALE",   price: "399", unit: "CHF", sub: "Maximal 4 Stunden" },
                  { label: "TAGESSATZ",             price: "699", unit: "CHF", sub: "Ganzer Tag pro Schreiner" },
                ].map(({ label, price, unit, sub }) => (
                  <div
                    key={label}
                    className="rounded-2xl bg-[#f5f5f5] p-6 flex flex-col items-center text-center hover:shadow-md transition-shadow"
                  >
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">
                      {label}
                    </p>
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-mono text-5xl font-black text-primary leading-none">
                        {price}
                      </span>
                      <span className="font-bold text-primary text-lg">{unit}</span>
                    </div>
                    <p className="mt-3 text-xs text-slate-500">{sub}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 border border-border/40 px-4 py-3">
                <span className="text-primary mt-0.5">ℹ</span>
                <p className="text-xs text-muted-foreground italic">
                  Die Preise verstehen sich pro eingesetztem Schreiner und Einsatzort.
                </p>
              </div>
            </div>
          </div>

          {/* Im Preis inbegriffen */}
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-8">
            <h3 className="text-base font-bold text-foreground mb-4 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" /> Im Preis immer inbegriffen:
            </h3>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {[
                "An- und Abfahrt",
                "Professionelles Werkzeug",
                "Aufbau nach Anleitung",
                "Entsorgung der Verpackungen",
                "Sauber hinterlassen",
                "Fachgerechte Montage aller Marken",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* @section: montage-qr */}
      <section className="py-14 bg-slate-50 border-y border-border/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-10 justify-center">
            {/* QR image */}
            <div className="shrink-0 rounded-2xl overflow-hidden shadow-xl border border-border/40">
              <img
                src={QR_URL}
                alt="QR Code Möbelmontage Preise"
                className="w-64 h-auto"
              />
            </div>
            {/* Text */}
            <div className="max-w-sm text-center md:text-left">
              <div className="inline-flex items-center gap-2 mb-3 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
                <QrCode className="h-3.5 w-3.5" />
                QR-Code
              </div>
              <h3 className="text-2xl font-bold text-foreground">
                Preisliste immer dabei
              </h3>
              <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                Scannen Sie den QR-Code mit Ihrem Smartphone, um sofort die aktuellen Fixpreise für die Möbelmontage zu sehen — und direkt eine Anfrage zu stellen.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                {[
                  "1 Stunde — CHF 199",
                  "2 Stunden — CHF 349",
                  "½ Tag (4h) — CHF 399",
                  "Ganzer Tag (8h) — CHF 699",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex flex-wrap gap-3 justify-center md:justify-start">
                {/* Download */}
                <a
                  href={QR_URL}
                  download="DS_Logistik_Montage_QR.png"
                  className="inline-flex items-center gap-2 rounded-full border border-primary/50 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary hover:text-white transition-colors"
                >
                  <QrCode className="h-4 w-4" /> Herunterladen
                </a>
                {/* WhatsApp share */}
                <a
                  href={`https://wa.me/?text=${encodeURIComponent("📦 DS Logistik – Möbelmontage Fixpreise:\n\n• 1 Stunde – CHF 199\n• 2 Stunden – CHF 349\n• ½ Tag (4h) – CHF 399\n• Ganzer Tag (8h) – CHF 699\n\nJetzt buchen 👉 https://c2ffenhghy.skywork.website/#/montage\n📞 079 888 32 62")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:scale-105 transition-transform shadow-sm"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp
                </a>
                {/* Email share */}
                <a
                  href={`mailto:?subject=${encodeURIComponent("DS Logistik – Möbelmontage Fixpreise")}&body=${encodeURIComponent("Guten Tag,\n\nHier sind die aktuellen Fixpreise für die Möbelmontage von DS Logistik:\n\n• 1 Stunde – CHF 199\n• 2 Stunden – CHF 349\n• ½ Tag (4 Stunden) – CHF 399\n• Ganzer Tag (8 Stunden) – CHF 699\n\nAlle Preise inkl. An-/Abfahrt & Verpackungsentsorgung.\n\nOnline buchen: https://c2ffenhghy.skywork.website/#/montage\n\nDS Logistik\nTel: 032 671 20 10\nWhatsApp: 079 888 32 62\nandre.stalder@ds-logistik.ch")}`}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-700 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900 transition-colors shadow-sm"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Per E-Mail senden
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* @section: montage-booking */}
      <section className="bg-muted/30 py-20" id="anfrage">
        <div className="mx-auto max-w-2xl px-5 lg:px-8">
          <div className="text-center mb-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">
              Jetzt buchen
            </p>
            <h2 className="mt-3 text-3xl font-bold text-foreground">
              Montage anfragen
            </h2>
            <p className="mt-2 text-muted-foreground">
              Ausgewähltes Paket:{" "}
              <strong className="text-primary">
                {pkg.duration} — CHF {pkg.price}
              </strong>
            </p>
          </div>

          {submitted ? (
            <div className="rounded-3xl border border-green-200 bg-green-50 p-10 text-center">
              <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-green-500" />
              <h3 className="text-xl font-bold text-foreground">
                Anfrage gesendet!
              </h3>
              <p className="mt-2 text-muted-foreground">
                Wir melden uns innerhalb von 24 Stunden bei Ihnen.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-4 rounded-3xl border border-border/60 bg-card p-6 sm:p-8 shadow-sm"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  name="name"
                  required
                  placeholder="Ihr Name *"
                  className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                <input
                  name="phone"
                  type="tel"
                  required
                  placeholder="Telefon *"
                  className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <input
                name="email"
                type="email"
                placeholder="E-Mail (optional)"
                className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <input
                name="address"
                required
                placeholder="Ihre Adresse (Strasse, PLZ, Ort) *"
                className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <input
                name="date"
                type="date"
                required
                className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <textarea
                name="notes"
                rows={3}
                placeholder="Welche Möbel sollen montiert werden? (optional)"
                className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
              />
              <button
                type="submit"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-white shadow-[0_8px_24px_-10px_rgba(200,25,11,0.6)] hover:-translate-y-px transition-all"
              >
                Anfrage senden <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Oder direkt anrufen / WhatsApp:
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-3">
              <a
                href="tel:+41326712010"
                className="inline-flex items-center gap-2 rounded-full border border-border/60 px-4 py-2 text-sm font-semibold hover:bg-muted transition-colors"
              >
                <Phone className="h-4 w-4 text-primary" /> 032 671 20 10
              </a>
              <a
                href="https://wa.me/41798883262"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:scale-105 transition-transform"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                079 888 32 62
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* @section: montage-reviews */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <h2 className="mb-8 text-center text-2xl font-bold text-foreground">
            Das sagen unsere Kunden
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              {
                name: "M. Berger",
                text: "Super Service! Der Monteur war pünktlich, hat alles sauber aufgebaut und die Verpackungen mitgenommen. Sehr empfehlenswert!",
              },
              {
                name: "S. Zimmermann",
                text: "Kleiderschrank und Bett in 2 Stunden fertig montiert. Professionell und freundlich. Werde wieder buchen!",
              },
              {
                name: "A. Müller",
                text: "Endlich eine unkomplizierte Lösung für Möbelmontage. Faire Preise und top Qualität. Danke DS Logistik!",
              },
            ].map(({ name, text }) => (
              <div
                key={name}
                className="rounded-2xl border border-border/60 bg-card p-5"
              >
                <div className="mb-2 flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      className="h-4 w-4 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>
                <p className="text-sm leading-6 text-muted-foreground">
                  "{text}"
                </p>
                <p className="mt-3 text-xs font-semibold text-foreground">
                  {name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Termin & Anfrage */}
      <section className="py-16 bg-muted/30 border-y border-border/40">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="text-center mb-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">Jetzt buchen</p>
            <h2 className="mt-3 text-3xl font-bold text-foreground">Termin anfragen</h2>
            <p className="mt-3 text-muted-foreground">Wählen Sie Service, Datum und Uhrzeit — wir melden uns innerhalb von 24 Stunden.</p>
          </div>
          <BookingForm defaultService="Professionelle Montage" />
        </div>
      </section>

      {/* @section: montage-footer */}
      <footer className="border-t border-border/40 bg-background py-6 text-center">
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} DS Logistik · Bielstrasse 20, 4500
          Solothurn ·{" "}
          <a href="/#/impressum" className="hover:text-primary">
            Impressum
          </a>
        </p>
      </footer>

      {/* WhatsApp floating button */}
      <a
        href="https://wa.me/41798883262"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-white shadow-lg hover:scale-105 transition-transform"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-6 w-6 fill-white"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        <span className="text-sm font-bold">WhatsApp</span>
      </a>
    </div>
  );
}
